const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

const parseDateKey = (value = '') => {
  const dateKey = normalizeText(value);
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatMomentDateLabel = (value = '') => {
  const date = parseDateKey(value);
  return date
    ? date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
};

const normalizeSectionLabel = (value = '') => {
  const label = normalizeText(value);
  return label === 'Time unclear' ? 'No exact time' : label;
};

const FALLBACK_CHAPTER = {
  key: 'story-general',
  label: 'Story',
  order: 0,
  source: 'fallback',
  sourceSectionIndex: null,
};

const getSceneSortTime = (scene = {}) => {
  const occurredOnDate = parseDateKey(scene.occurredOnDate || scene.metadata?.timelinePlanner?.occurredOnDate || '');
  if (occurredOnDate) return occurredOnDate.getTime();
  const narrativeSortTime = Number(scene.narrativeSortTime);
  if (Number.isFinite(narrativeSortTime)) return narrativeSortTime;
  const rawTime = scene.timestamp?.toDate?.() || scene.timestamp || scene.createdAt;
  const parsedTime = rawTime ? new Date(rawTime).getTime() : Number.NaN;
  return Number.isFinite(parsedTime) ? parsedTime : Number.MAX_SAFE_INTEGER;
};

// Backend (timelineStoryChapterAlignment.js) is the sole authority on which chapter
// a scene belongs to, its order, and its label. This module only renders that
// decision — it never re-derives chapter assignment.
const getSceneChapterMetadata = (scene = {}) => (
  scene.storyChapter
  || scene.metadata?.timelinePlanner?.storyChapter
  || scene.timelinePlanner?.storyChapter
  || null
);

const getSceneSummaryMetadata = (scene = {}) => (
  scene.storySummary
  || scene.metadata?.timelinePlanner?.storySummary
  || scene.timelinePlanner?.storySummary
  || null
);

// The backend persists chapter identity (key/label/order/sourceSectionIndex) but not
// each chapter's raw author paragraph text. Recovering that paragraph text requires
// splitting the original note the same way the backend did. This is pure text
// segmentation keyed off the backend's own sourceSectionIndex — it never decides
// which chapter a scene belongs to, so it is not a competing classification.
const cleanHeading = (value = '') => normalizeText(value)
  .replace(/^\d+[.)]\s+/, '')
  .replace(/:+$/, '');

const isBulletLikeLine = (value = '') => /^[-*•]|^(?:\[[ xX]\]|[☑✓✔✅☐□])\s+/u.test(normalizeText(value));

const isLikelyAuthorHeading = (value = '') => {
  const text = cleanHeading(value);
  if (!text) return false;
  if (isBulletLikeLine(text)) return false;
  if (text.length > 80) return false;
  if (/[.!?]$/.test(text)) return false;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 8) return false;

  if (/[:]/.test(value) && words.length <= 8) return true;
  if (/&|\//.test(text)) return true;
  if (words.length <= 4) return true;

  const capitalizedWords = words.filter((word) => /^[A-Z]/.test(word)).length;
  return capitalizedWords >= Math.ceil(words.length / 2);
};

const parseAuthorSectionBodiesByIndex = (rawText = '') => {
  const lines = String(rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const sections = [];
  let current = null;

  const flush = () => {
    if (!current) return;
    sections.push(normalizeText(current.join(' ')));
    current = null;
  };

  lines.forEach((line) => {
    const text = normalizeText(line);
    if (!text) return;

    if (isLikelyAuthorHeading(text)) {
      flush();
      current = [];
      return;
    }

    if (!current) return;
    current.push(text);
  });

  flush();

  return sections;
};

export const buildTimelineStoryPresentation = (scenes = [], { originalNote = '' } = {}) => {
  const orderedScenes = [...scenes].sort((left, right) => getSceneSortTime(left) - getSceneSortTime(right));
  const summary = orderedScenes
    .map((scene) => getSceneSummaryMetadata(scene))
    .find((item) => normalizeText(item?.text)) || null;

  const authorSectionBodies = parseAuthorSectionBodiesByIndex(originalNote);

  const chapterMap = new Map();
  const chapterKeyOrder = [];

  orderedScenes.forEach((scene) => {
    const chapter = getSceneChapterMetadata(scene) || FALLBACK_CHAPTER;
    const key = chapter.key || FALLBACK_CHAPTER.key;

    if (!chapterMap.has(key)) {
      const bodyText = chapter.source === 'author_heading' && Number.isInteger(chapter.sourceSectionIndex)
        ? (authorSectionBodies[chapter.sourceSectionIndex] || '')
        : '';

      chapterMap.set(key, {
        key,
        bucket: key,
        label: normalizeSectionLabel(chapter.label || FALLBACK_CHAPTER.label),
        order: Number.isFinite(chapter.order) ? chapter.order : chapterKeyOrder.length,
        source: chapter.source || FALLBACK_CHAPTER.source,
        sourceSectionIndex: Number.isInteger(chapter.sourceSectionIndex) ? chapter.sourceSectionIndex : null,
        bodyText,
        scenes: [],
      });
      chapterKeyOrder.push(key);
    }

    chapterMap.get(key).scenes.push(scene);
  });

  const sections = chapterKeyOrder
    .map((key) => chapterMap.get(key))
    .sort((left, right) => left.order - right.order)
    .map((section) => ({
      ...section,
      kind: section.source === 'author_heading' || section.scenes.length >= 2 ? 'chapter' : 'moment',
    }));

  return { summary, sections };
};

export const buildTimelineStorySections = (scenes = [], options = {}) => buildTimelineStoryPresentation(scenes, options).sections;
